<template>
  <div>
    <b-card no-body>
      <b-row class="mx-0" align-h="between" align-v="center">
        <b-col class="px-0" lg="4" md="12" sm="12">
          <h5>Related Events</h5>
        </b-col>
      </b-row>

      <b-table
        id="endpoint-threat-events-table"
        class="table-with-actions-min-height"
        responsive
        small
        primary-key="id"
        :items="events"
        :fields="tableFields"
        :per-page="perPage"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortAsc"
        selectable
        select-mode="single"
        @row-selected="onRowSelected"
      >
        <template #cell(severity)="row">
          <b-badge pill :variant="alertStatusVariant(row.value)">
            {{ alertStatusText(row.value) }}
          </b-badge>
        </template>

        <template #cell(processes)="{ item }">
          {{ (item.processes || []).join(', ') }}
        </template>
        <template #row-details="row">
          <div v-if="row.item._showDetails" class="px-1 py-1">
            <b-table-simple small bordered responsive>
              <b-tbody>
                <b-tr>
                  <b-th class="align-top" style="width: 180px;">Time</b-th>
                  <b-td class="text-monospace">
                    {{ row.item.time || '' }}
                  </b-td>
                </b-tr>

                <b-tr v-if="row.item.description">
                  <b-th class="align-top">Description</b-th>
                  <b-td style="white-space: pre-wrap;">
                    {{ row.item.description }}
                  </b-td>
                </b-tr>

                <b-tr v-if="row.item.patternIds && row.item.patternIds.length">
                  <b-th class="align-top">STIX SDO IDs</b-th>
                  <b-td class="text-monospace">
                    <div
                      v-for="id in visibleIds(row.item.patternIds, row.item.id, 'pattern')"
                      :key="`sdo-${row.item.id}-${id}`"
                    >
                      {{ id }}
                    </div>

                    <b-button
                      v-if="row.item.patternIds.length > 10"
                      size="sm"
                      variant="link"
                      class="p-0 mt-1"
                      @click="toggleIdsExpanded(row.item.id, 'pattern')"
                    >
                      {{ isExpanded(row.item.id, 'pattern') ? 'Show less' : 'Load more' }}
                    </b-button>
                  </b-td>
                </b-tr>

                <b-tr v-if="row.item.stixIds && row.item.stixIds.length">
                  <b-th class="align-top">STIX Indicator IDs</b-th>
                  <b-td class="text-monospace">
                    <div
                      v-for="id in visibleIds(row.item.stixIds, row.item.id, 'stix')"
                      :key="`stix-${row.item.id}-${id}`"
                    >
                      {{ id }}
                    </div>

                    <b-button
                      v-if="row.item.stixIds.length > 10"
                      size="sm"
                      variant="link"
                      class="p-0 mt-1"
                      @click="toggleIdsExpanded(row.item.id, 'stix')"
                    >
                      {{ isExpanded(row.item.id, 'stix') ? 'Show less' : 'Load more' }}
                    </b-button>
                  </b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </div>
        </template>
      </b-table>

      <b-pagination
        v-if="paginationTotalRows > perPage"
        v-model="currentPage"
        :total-rows="paginationTotalRows"
        :per-page="perPage"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
        align="right"
        class="mb-0 mt-1"
        ariaControls="endpoint-threat-events-table"
      />
    </b-card>
  </div>
</template>

<script>
import alertSeverities from '@/xvisor/constants/alertSeverities';
import colorPalette from '@/xvisor/constants/colorPalette';

const safeArr = (v) => (Array.isArray(v) ? v : []);
const strOrEmpty = (v) => (typeof v === 'string' ? v : '');

export default {
  name: 'EndpointThreatEventsTable',
  props: {
    alerts: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      events: [],
      enrichExpanded: {
        pattern: {},
        stix: {},
      },
      perPage: 5,
      currentPage: 1,
      tableFields: [
        { key: 'severity', label: 'Severity', sortable: true },
        { key: 'file', label: 'File', sortable: true },
        { key: 'processes', label: 'Processes', sortable: true },
        { key: 'patternName', label: 'patterns.name', sortable: true },
        { key: 'count', label: 'Count', sortable: true },
      ],
      colorPalette,
      sortBy: 'severity',
      sortAsc: false,
      selected: null,
    };
  },
  mounted() {
    this.buildEvents();
  },
  watch: {
    alerts: {
      deep: true,
      handler() {
        this.buildEvents();
      },
    },
  },
  computed: {
    paginationTotalRows() {
      return this.events.length;
    },
  },
  methods: {
    buildEvents() {
      const rows = safeArr(this.alerts).flatMap((alert) => {
        const file = strOrEmpty(alert?.metadata?.file?.path);
        const processes = safeArr(alert?.metadata?.endpoints?.[0]?.processes);
        const stixIocs = alert?.metadata?.stixIocs || {};
        const stixIds = safeArr(stixIocs?.ids);
        const patterns = safeArr(stixIocs?.patterns);

        const base = {
          alertId: alert?.id,
          time: alert?.time,
          severity: alert?.severity,
          file,
          processes,
          stixIds,
          _showDetails: false,
        };

        if (!patterns.length) {
          return [
            {
              ...base,
              id: `${base.alertId || 'alert'}-nopattern-${file}`,
              patternName: '',
              description: '',
              patternIds: [],
              count: 1,
            },
          ];
        }

        return patterns.map((p) => ({
          ...base,
          id: `${base.alertId || 'alert'}-${strOrEmpty(p?.name) || 'pattern'}-${file}`,
          patternName: strOrEmpty(p?.name),
          description: strOrEmpty(p?.description),
          patternIds: safeArr(p?.ids),
          count: 1,
        }));
      });

      const keyOf = (r) => ([
        r.severity,
        r.file,
        safeArr(r.processes).join(','),
        r.patternName,
        safeArr(r.patternIds).join(','),
        safeArr(r.stixIds).join(','),
      ].join('|'));

      const aggregatedMap = rows.reduce((map, r) => {
        const key = keyOf(r);
        const existing = map.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          map.set(key, { ...r });
        }
        return map;
      }, new Map());

      this.events = Array.from(aggregatedMap.values());
      this.currentPage = 1;
      this.selected = null;
      this.enrichExpanded = { pattern: {}, stix: {} };
    },
    alertStatusVariant(status) {
      if (status === alertSeverities.critical) return 'danger';
      if (status === alertSeverities.warning) return 'warning';
      return 'success';
    },
    alertStatusText(status) {
      if (status === alertSeverities.critical) return 'Critical';
      if (status === alertSeverities.warning) return 'Warning';
      return 'Notice';
    },
    isExpanded(rowId, type) {
      const bucket = this.enrichExpanded?.[type] || {};
      return Boolean(bucket[rowId]);
    },
    visibleIds(ids, rowId, type) {
      const list = Array.isArray(ids) ? ids : [];
      return this.isExpanded(rowId, type) ? list : list.slice(0, 10);
    },
    toggleIdsExpanded(rowId, type) {
      const current = this.isExpanded(rowId, type);
      if (!this.enrichExpanded[type]) this.$set(this.enrichExpanded, type, {});
      this.$set(this.enrichExpanded[type], rowId, !current);
    },
    onRowSelected(selectedItems) {
      this.events = this.events.map((row) => ({ ...row, _showDetails: false }));

      const [first] = selectedItems;
      if (!first) {
        this.selected = null;
        this.enrichExpanded = { pattern: {}, stix: {} };
        return;
      }

      const { id: selectedId } = first;
      this.events = this.events.map((row) => (
        row.id === selectedId ? { ...row, _showDetails: true } : row
      ));

      this.enrichExpanded = { pattern: {}, stix: {} };
      this.selected = first;
    },
  },
};
</script>
